/**
 * CustomersTab - Customer list tab content
 *
 * Displays a searchable, paginated table of all customers.
 * Search by Consent ID only.
 */

import React, { useEffect } from 'react';
import { Sparkles } from 'lucide-react';
import { SearchBar, Loader } from 'equal-ds-ui';
import { CustomersTabProps } from '../../../types/ews.types';
import { EWSCustomersTable } from './EWSCustomersTable';

export const CustomersTab: React.FC<CustomersTabProps> = ({
  onPageChange,
  onPageSizeChange,
  searchQuery,
  onSearch,
  customers,
  pagination,
  isLoading,
  onCustomerClick,
  sortBy,
  sortOrder,
  onSortChange,
}) => {
  const [localQuery, setLocalQuery] = React.useState(searchQuery);

  // Sync local state with props
  useEffect(() => {
    setLocalQuery(searchQuery);
  }, [searchQuery]);

  // Debounced search effect
  useEffect(() => {
    const handler = window.setTimeout(() => {
      onSearch(localQuery, 'consentId');
    }, 400);
    return () => window.clearTimeout(handler);
  }, [localQuery, onSearch]);

  return (
    <div className="flex-1 min-h-0 flex flex-col p-4 border border-border-default rounded-xl shadow-lg bg-white">
      {/* Header with Search and Filter Badge */}
      <div className="flex flex-col items-start space-y-4 py-4">
        <div className="flex items-center justify-between w-full">
          <div className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-blue-500" />
            <h3 className="text-lg font-semibold text-gray-900">All Customers</h3>
          </div>
        </div>
        <div className="w-80">
          <SearchBar
            variant="simple"
            value={localQuery}
            onChange={setLocalQuery}
            placeholder="Search by Consent ID..."
          />
        </div>
      </div>

      {/* Customer Table */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
        {customers && pagination ? (
          <EWSCustomersTable
            customers={customers}
            isLoading={isLoading}
            pagination={pagination}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
            onRowClick={onCustomerClick}
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortChange={onSortChange}
          />
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader size={72} cellSize={20} />
          </div>
        ) : (
          <div className="flex items-center justify-center py-12">
            <div className="text-gray-500">No customers found</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomersTab;
